import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Radio,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Tooltip,
} from "@mui/material";
import TextInput from "../../../../components/textinput/textinput";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DialogContainer from "../../../../components/dialog-container/dialog-container";
import { permissionAccessLevels, userType } from "../../../../utils/Constant";
import { useDispatch, useSelector } from "react-redux";
import { getAllClientRolesRequest } from "../../../../redux/users/slice";
import Loading from "../../../../components/loading/loading";
import { addInternalUserRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import { useFormik } from "formik";
import * as yup from "yup";

type props = {
  showDialog: boolean;
  closeDialog: any;
  addUser?: any;
};

type FormValues = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  // roles: string[];
};

const ValiditionSchema = yup.object({
  title: yup.string().required("This field can't be empty"),
  firstName: yup.string().required("This field can't be empty"),
  lastName: yup.string().required("This field can't be empty"),
  email: yup
    .string()
    .required("This field can't be empty")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid Email Address"
    ),
  // roles: yup.array().min(1, "At least one role must be selected"),
});

export default function CreateInternalUser({
  showDialog,
  closeDialog,
  addUser,
}: props) {
  const [categoryNames, setCategoryNames] = useState<string[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [rolesError, setRolesError] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();
  const { loading } = useSelector((state: any) => state.user);
  const { allClientRoles } = useSelector((state: any) => state.user.user);

  const formik = useFormik<FormValues>({
    initialValues: {
      title: "",
      firstName: "",
      lastName: "",
      email: "",
      // roles: [],
    },
    validationSchema: ValiditionSchema,
    validate(values) {
      const roles = getSelectedRoles();
      if (roles.length === 0) {
        setRolesError(true);
      } else {
        setRolesError(false);
      }
    },
    onSubmit: (values) => {
      const roleIds = getSelectedRoles();
      if (roleIds.length > 0) {
        addInternalUser({ ...values, roleIds });
      } else {
        setRolesError(true);
      }
    },
  });

  const getSelectedRoles = () => {
    const selectedRoles: string[] = [];
    roles.forEach((role) => {
      if (role.checked) {
        selectedRoles.push(role.id);
      }
    });
    return selectedRoles;
  };

  useEffect(() => {
    dispatch(getAllClientRolesRequest());
  }, [dispatch]);

  useEffect(() => {
    const roles: any[] = [];
    allClientRoles?.map((role: any) => roles.push({ ...role, checked: false }));
    setRoles(roles);
  }, [allClientRoles]);

  const setUniqueCategory = () => {
    const categoryNames: string[] = [];
    roles.forEach((role) => {
      if (role.checked) {
        role.permissions.forEach((permission: any) => {
          if (!categoryNames.includes(permission.category.name)) {
            categoryNames.push(permission.category.name);
          }
        });
      }
    });
    setCategoryNames(categoryNames);
  };

  const getCategoryPermissions = (categoryName: string) => {
    let categoryPermissions: any[] = [];
    const filterPermissions = roles?.map((role: any) => {
      let checkedPermissions = [];
      if (role.checked) {
        checkedPermissions = role.permissions.filter(
          (permission: any) => permission?.category?.name === categoryName
        );
      }
      return checkedPermissions;
    });
    const flatFilteredPermissions = filterPermissions.flat(1);

    flatFilteredPermissions.forEach((permission) => {
      if (categoryPermissions.length > 0) {
        const isAvailable = checkPermissionAvailable(
          categoryPermissions,
          permission
        );
        if (!isAvailable) {
          categoryPermissions.push(permission);
        }
      } else {
        categoryPermissions.push(permission);
      }
    });

    return categoryPermissions;
  };

  const checkPermissionAvailable = (
    allPermissions: any[],
    checkPermission: any
  ) => {
    let isAvailable = false;
    allPermissions.forEach((permission) => {
      if (permission.name === checkPermission.name) {
        isAvailable = true;
      }
    });
    return isAvailable;
  };

  const onRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRolesError(false);
    roles.forEach((role) => {
      if (role.id === event.target.id) {
        role.checked = event.target.checked;
      }
    });
    setRoles(roles);
    setUniqueCategory();
  };

  const addInternalUser = async (data: any) => {
    const selectedRoles: string[] = [];
    roles.forEach((role) => {
      if (role.checked) {
        selectedRoles.push(role.id);
      }
    });
    const payload = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      roleIds: data.roleIds,
      title: data.title,
      userType: userType.tenant,
    };
    setShowLoading(true);
    const addUserResponse = await addInternalUserRequest(payload);
    setShowLoading(false);
    if (addUserResponse?.status === 201) {
      addUser(addUserResponse?.data);
      closeDialog();
    } else if (addUserResponse?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(addUserResponse?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Add Internal User"
      showDialog={showDialog}
      closeDialog={closeDialog}
      buttonText="Add User"
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <Typography className="user-details-txt">User Details</Typography>
          <Box className="user-name-layout">
            <Typography className="label-txt">Title</Typography>
            <TextInput
              className="field-input"
              id="title"
              name="title"
              label="Title"
              variant="outlined"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />
          </Box>
          <Divider />
          <Box className="field-layout">
            <Typography className="label-txt">Name</Typography>
            <Box sx={{ display: "flex", width: "calc(100% - 180px)" }}>
              <TextInput
                sx={{ flex: 1, mr: 2 }}
                id="firstName"
                name="firstName"
                label="First Name"
                variant="outlined"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
              <TextInput
                sx={{ flex: 1 }}
                id="lastName"
                name="lastName"
                label="Last Name"
                variant="outlined"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Box>
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="label-txt">Email</Typography>
            <TextInput
              className="field-input"
              id="email"
              name="email"
              label="Email"
              variant="outlined"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Box>
          <Divider />

          <Typography className="user-details-txt">Roles</Typography>
          <Box className="user-name-layout">
            <Typography className="label-txt">Role(s)</Typography>
            <FormGroup sx={{ display: "block", width: 620 }}>
              {roles.map((item) => (
                <FormControlLabel
                  sx={{ width: 300 }}
                  key={item.id}
                  control={
                    <Checkbox
                      name="roles"
                      value={item.name}
                      id={item.id}
                      checked={item.checked}
                      onChange={onRoleChange}
                    />
                  }
                  label={item.name}
                />
              ))}
            </FormGroup>
          </Box>
          {rolesError && (
            <Typography color="error" variant="body2" sx={{ ml: 25, mt: -2 }}>
              At least one role must be selected
            </Typography>
          )}

          {categoryNames.length > 0 && (
            <>
              <Divider />
              <Accordion
                sx={{ border: "none", boxShadow: "none" }}
                defaultExpanded
              >
                <AccordionSummary
                  sx={{
                    marginTop: -2,
                    padding: 0,
                    minHeight: 48,
                    ".MuiAccordionSummary-content.Mui-expanded": { margin: 0 },
                  }}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="permission-content"
                  id="permission-header"
                >
                  <Typography className="user-details-txt">
                    Permissions
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box
                    className="permission-header"
                    sx={{ width: "calc(100% - 192px)", marginLeft: 24 }}
                  >
                    <Typography
                      sx={{ flex: 1, textAlign: "start", fontWeight: "bold" }}
                    >
                      Permission
                    </Typography>
                    <Typography className="permission-header-txt">
                      Full Access
                    </Typography>
                    <Typography className="permission-header-txt">
                      View Only
                    </Typography>
                    <Typography className="permission-header-txt">
                      None
                    </Typography>
                  </Box>

                  {categoryNames?.map((categoryName: any, index: number) => {
                    const permissions = getCategoryPermissions(categoryName);
                    return (
                      <Box key={categoryName}>
                        {index > 0 && <Divider />}
                        <Box className="user-name-layout">
                          <Typography className="label-txt" sx={{ mt: 2 }}>
                            {categoryName}
                          </Typography>
                          <Box
                            display={"block"}
                            sx={{ width: "calc(100% - 192px)" }}
                          >
                            {permissions.map((item: any, index: number) => (
                              <Box key={item.key}>
                                <Box className="permission-row">
                                  <Box className="permission-title">
                                    <Typography>{item.name}</Typography>
                                    {index === 0 && (
                                      <Tooltip
                                        title="Description for more complicated and involved permissions"
                                        arrow
                                        placement="top"
                                      >
                                        <IconButton>
                                          <InfoOutlinedIcon color="primary" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Radio
                                    className="permission-radio"
                                    value="full-access"
                                    name="radio-buttons"
                                    inputProps={{ "aria-label": "A" }}
                                    checked={
                                      item.accessLevel ===
                                      permissionAccessLevels.fullAccess
                                    }
                                    disabled
                                  />
                                  <Radio
                                    className="permission-radio"
                                    value="view-only"
                                    name="radio-buttons"
                                    inputProps={{ "aria-label": "B" }}
                                    checked={
                                      item.accessLevel ===
                                      permissionAccessLevels.viewOnly
                                    }
                                    disabled
                                  />
                                  <Radio
                                    className="permission-radio"
                                    value="none"
                                    name="radio-buttons"
                                    inputProps={{ "aria-label": "C" }}
                                    checked={
                                      item.accessLevel ===
                                      permissionAccessLevels.noAccess
                                    }
                                    disabled
                                  />
                                </Box>
                                {index !== permissions.length - 1 && (
                                  <Divider />
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>
      </form>
      {(loading || showLoading) && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </DialogContainer>
  );
}
